import React from 'react';
import FolderItem from './FolderItem';
import FileItem from './FileItem';
import { useTheme } from '../context/ThemeContext';

const ExplorerBody = ({
  viewMode,
  selectedFolder,
  selectedSubfolder,
  iconViewPath,
  selectedIconItem,
  handleFolderClick,
  handleSubfolderClick,
  handleFileClick,
  handleCaseStudyClick,
  handleIconItemDoubleClick,
  handleIconItemClick,
  handleBreadcrumbClick,
  FOLDERS,
  FILES,
  CASE_STUDIES,
  selectedFile,
  selectedCaseStudy
}) => {
  const { colors } = useTheme();

  return (
    <div className="mt-4 pl-2 sm:pl-3 lg:pl-4 pr-2 sm:pr-3 lg:pr-4 flex-1 overflow-auto relative" style={{ scrollbarWidth: 'thin', scrollbarColor: `${colors.border} ${colors.background}` }}>
      {/* Bottom dashed border - Desktop only, scrolls with content */}
      <div
        className="hidden lg:block absolute bottom-0 left-0 right-0 h-0 w-full"
        style={{
          borderImageSource: `repeating-linear-gradient(to right, ${colors.border} 0, ${colors.border} 2px, transparent 2px, transparent 6px)`,
          borderImageSlice: 1
        }}
      ></div>

      {/* Desktop: Hierarchical Tree View */}
      {viewMode === 'tree' && (
        <div className="hidden lg:block space-y-[20px]">
          {Object.keys(FOLDERS).map((folderName) => (
            <div key={folderName}>
              <FolderItem
                name={folderName}
                isOpen={selectedFolder === folderName}
                hasArrow={FOLDERS[folderName].hasArrow}
                onClick={() => handleFolderClick(folderName)}
              />
              {selectedFolder === folderName && (
                <div className="ml-6 mt-[16px] space-y-[20px]">
                  {/* Show subfolders if they exist */}
                  {FOLDERS[folderName].subfolders.length > 0 && FOLDERS[folderName].subfolders.map((subfolderName) => (
                    <div key={subfolderName}>
                      <FolderItem
                        name={subfolderName}
                        isOpen={selectedSubfolder === subfolderName}
                        hasArrow={subfolderName === 'About' || subfolderName === 'Projects'}
                        onClick={() => handleSubfolderClick(subfolderName)}
                      />
                      {selectedSubfolder === subfolderName && (
                        <div className="ml-6 mt-[16px] space-y-[12px]">
                          {subfolderName === 'Projects' && CASE_STUDIES['Projects'] ? (
                            Object.keys(CASE_STUDIES['Projects']).map((caseStudyName) => (
                              <FileItem
                                key={caseStudyName}
                                name={caseStudyName}
                                isSelected={selectedCaseStudy === caseStudyName}
                                onClick={() => handleCaseStudyClick(caseStudyName)}
                              />
                            ))
                          ) : (
                            FILES[subfolderName]?.map((fileName) => (
                              <FileItem
                                key={fileName}
                                name={fileName}
                                isSelected={selectedFile === fileName}
                                onClick={() => handleFileClick(fileName)}
                              />
                            ))
                          )}
                        </div>
                      )}
                    </div>
                  ))}

                  {/* Show files directly in the folder if no subfolders */}
                  {FOLDERS[folderName].subfolders.length === 0 && FILES[folderName]?.map((fileName) => (
                    <FileItem
                      key={fileName}
                      name={fileName}
                      isSelected={selectedFile === fileName}
                      onClick={() => handleFileClick(fileName)}
                    />
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Desktop: List View - 3 Column Grid */}
      {viewMode === 'list' && (
        <div className="hidden lg:grid grid-cols-3 gap-6">
          {/* Column 1 - Main Folders */}
          <div className="space-y-[12px]">
            {Object.keys(FOLDERS).map((folderName) => (
              <div key={folderName} className="flex items-center gap-2 cursor-pointer hover:opacity-80" onClick={() => handleFolderClick(folderName)}>
                <div
                  style={{
                    width: '19px',
                    height: '16px',
                    flexShrink: 0,
                    backgroundColor: colors.textPrimary,
                    maskImage: 'url(/folders/folder-closed.svg)',
                    WebkitMaskImage: 'url(/folders/folder-closed.svg)',
                    maskSize: 'contain',
                    WebkitMaskSize: 'contain',
                    maskRepeat: 'no-repeat',
                    WebkitMaskRepeat: 'no-repeat'
                  }}
                />
                <span className="text-[20px]" style={{ color: colors.textSecondary }}>{folderName}</span>
                {FOLDERS[folderName].hasArrow && selectedFolder === folderName && (
                  <div
                    style={{
                      width: '12px',
                      height: '12px',
                      marginLeft: 'auto',
                      flexShrink: 0,
                      backgroundColor: colors.textPrimary,
                      maskImage: 'url(/folders/next-button.svg)',
                      WebkitMaskImage: 'url(/folders/next-button.svg)',
                      maskSize: 'contain',
                      WebkitMaskSize: 'contain',
                      maskRepeat: 'no-repeat',
                      WebkitMaskRepeat: 'no-repeat'
                    }}
                  />
                )}
              </div>
            ))}
          </div>

          {/* Column 2 - Subfolders OR Files (if no subfolders) */}
          <div className="space-y-[12px]">
            {selectedFolder && (
              FOLDERS[selectedFolder].subfolders.length > 0 ? (
                // Show Subfolders
                FOLDERS[selectedFolder].subfolders.map((subfolderName) => (
                  <div key={subfolderName} className="flex items-center gap-2 cursor-pointer hover:opacity-80" onClick={() => handleSubfolderClick(subfolderName)}>
                    <div
                      style={{
                        width: '19px',
                        height: '16px',
                        flexShrink: 0,
                        backgroundColor: colors.textPrimary,
                        maskImage: 'url(/folders/folder-closed.svg)',
                        WebkitMaskImage: 'url(/folders/folder-closed.svg)',
                        maskSize: 'contain',
                        WebkitMaskSize: 'contain',
                        maskRepeat: 'no-repeat',
                        WebkitMaskRepeat: 'no-repeat'
                      }}
                    />
                    <span className="text-[20px]" style={{ color: colors.textSecondary }}>{subfolderName}</span>
                    {(subfolderName === 'About' || subfolderName === 'Projects') && selectedSubfolder === subfolderName && (
                      <div
                        style={{
                          width: '12px',
                          height: '12px',
                          marginLeft: 'auto',
                          flexShrink: 0,
                          backgroundColor: colors.textPrimary,
                          maskImage: 'url(/folders/next-button.svg)',
                          WebkitMaskImage: 'url(/folders/next-button.svg)',
                          maskSize: 'contain',
                          WebkitMaskSize: 'contain',
                          maskRepeat: 'no-repeat',
                          WebkitMaskRepeat: 'no-repeat'
                        }}
                      />
                    )}
                  </div>
                ))
              ) : (
                // Show Files directly (e.g. Thoughts folder)
                FILES[selectedFolder]?.map((fileName) => (
                  <FileItem
                    key={fileName}
                    name={fileName}
                    isSelected={selectedFile === fileName}
                    onClick={() => handleFileClick(fileName)}
                  />
                ))
              )
            )}
          </div>

          {/* Column 3 - Files (only if subfolders exist) */}
          <div className="space-y-[12px]">
            {selectedFolder && FOLDERS[selectedFolder].subfolders.length > 0 && (
              selectedSubfolder === 'Projects' && CASE_STUDIES['Projects'] ? (
                Object.keys(CASE_STUDIES['Projects']).map((caseStudyName) => (
                  <FileItem
                    key={caseStudyName}
                    name={caseStudyName}
                    isSelected={selectedCaseStudy === caseStudyName}
                    onClick={() => handleCaseStudyClick(caseStudyName)}
                  />
                ))
              ) : (
                // Show files from selected subfolder
                FILES[selectedSubfolder]?.map((fileName) => (
                  <FileItem
                    key={fileName}
                    name={fileName}
                    isSelected={selectedFile === fileName}
                    onClick={() => handleFileClick(fileName)}
                  />
                ))
              )
            )}
          </div>
        </div>
      )}

      {/* Desktop: Icon View - Only in second column */}
      {viewMode === 'icon' && (
        <div className="hidden lg:block">
          {/* Icon Grid */}
          <div className="grid grid-cols-4 gap-6 p-0">
            {iconViewPath.length === 0 ? (
              Object.keys(FOLDERS).map((folderName) => (
                <div
                  key={folderName}
                  className={`flex flex-col items-center gap-2 cursor-pointer hover:opacity-80 p-2 rounded transition-colors`}
                  style={{ backgroundColor: selectedIconItem?.name === folderName ? colors.folderHighlight : 'transparent' }}
                  onClick={() => handleIconItemClick(folderName, 'folder')}
                  onDoubleClick={() => handleIconItemDoubleClick(folderName, 'folder')}
                >
                  <div
                    style={{
                      width: '32px',
                      height: '28px',
                      backgroundColor: colors.textPrimary,
                      maskImage: 'url(/folders/large-folder.svg)',
                      WebkitMaskImage: 'url(/folders/large-folder.svg)',
                      maskSize: 'contain',
                      WebkitMaskSize: 'contain',
                      maskRepeat: 'no-repeat',
                      WebkitMaskRepeat: 'no-repeat'
                    }}
                  />
                  <span className={`text-[20px] text-center w-full break-words leading-tight ${selectedIconItem?.name === folderName ? 'font-medium' : ''}`} style={{ color: selectedIconItem?.name === folderName ? colors.textPrimary : colors.textSecondary }}>{folderName}</span>
                </div>
              ))
            ) : (
              (() => {
                const currentFolder = iconViewPath[iconViewPath.length - 1];
                const subfolders = FOLDERS[currentFolder]?.subfolders || [];
                const fileEntries = [];

                // Add files from the current folder if it has any
                if (FILES[currentFolder]) {
                  fileEntries.push(...FILES[currentFolder].map(file => ({ name: file, type: 'file' })));
                }

                // Also check if we should show files from a selected subfolder
                if (selectedSubfolder && FILES[selectedSubfolder]) {
                  fileEntries.push(...FILES[selectedSubfolder].map(file => ({ name: file, type: 'file' })));
                }

                if (currentFolder === 'Projects' && CASE_STUDIES['Projects']) {
                  fileEntries.push(
                    ...Object.keys(CASE_STUDIES['Projects']).map((caseStudyName) => ({
                      name: caseStudyName,
                      type: 'caseStudy'
                    }))
                  );
                }

                return (
                  <>
                    {subfolders.map((subfolder) => (
                      <div
                        key={subfolder}
                        className={`flex flex-col items-center gap-2 cursor-pointer hover:opacity-80 p-2 rounded transition-colors`}
                        style={{ backgroundColor: selectedIconItem?.name === subfolder ? colors.folderHighlight : 'transparent' }}
                        onClick={() => handleIconItemClick(subfolder, 'folder')}
                        onDoubleClick={() => handleIconItemDoubleClick(subfolder, 'folder')}
                      >
                        <div
                          style={{
                            width: '32px',
                            height: '28px',
                            backgroundColor: colors.textPrimary,
                            maskImage: 'url(/folders/large-folder.svg)',
                            WebkitMaskImage: 'url(/folders/large-folder.svg)',
                            maskSize: 'contain',
                            WebkitMaskSize: 'contain',
                            maskRepeat: 'no-repeat',
                            WebkitMaskRepeat: 'no-repeat'
                          }}
                        />
                        <span className={`text-[20px] text-center w-full break-words leading-tight ${selectedIconItem?.name === subfolder ? 'font-medium' : ''}`} style={{ color: selectedIconItem?.name === subfolder ? colors.textPrimary : colors.textSecondary }}>{subfolder}</span>
                      </div>
                    ))}

                    {fileEntries.map((fileEntry) => (
                      <div
                        key={fileEntry.name}
                        className={`flex flex-col items-center gap-2 cursor-pointer hover:opacity-80 p-2 rounded transition-colors`}
                        style={{ backgroundColor: selectedIconItem?.name === fileEntry.name ? colors.folderHighlight : 'transparent' }}
                        onClick={() => handleIconItemClick(fileEntry.name, fileEntry.type)}
                        onDoubleClick={() => handleIconItemDoubleClick(fileEntry.name, fileEntry.type)}
                      >
                        <div
                          style={{
                            width: '32px',
                            height: '28px',
                            backgroundColor: colors.textPrimary,
                            maskImage: `url(${fileEntry.type === 'folder' ? '/folders/large-folder.svg' : '/folders/document-icon.svg'})`,
                            WebkitMaskImage: `url(${fileEntry.type === 'folder' ? '/folders/large-folder.svg' : '/folders/document-icon.svg'})`,
                            maskSize: 'contain',
                            WebkitMaskSize: 'contain',
                            maskRepeat: 'no-repeat',
                            WebkitMaskRepeat: 'no-repeat'
                          }}
                        />
                        <span className={`text-[20px] text-center w-full break-words leading-tight ${selectedIconItem?.name === fileEntry.name ? 'font-medium' : ''}`} style={{ color: selectedIconItem?.name === fileEntry.name ? colors.textPrimary : colors.textSecondary }}>{fileEntry.name}</span>
                      </div>
                    ))}
                  </>
                );
              })()
            )}
          </div>
        </div>
      )}

      {/* Mobile: Hierarchical tree layout */}
      <div className="lg:hidden space-y-[20px]">
        {/* Root Folders */}
        {Object.keys(FOLDERS).map((folderName) => (
          <div key={folderName}>
            {/* Main Folder */}
            <FolderItem
              name={folderName}
              isOpen={selectedFolder === folderName}
              hasArrow={FOLDERS[folderName].hasArrow}
              onClick={() => handleFolderClick(folderName)}
            />

            {/* Subfolders - Indented */}
            {selectedFolder === folderName && (
              <div className="ml-6 space-y-[20px] mt-[12px]">
                {FOLDERS[folderName].subfolders.length > 0 ? (
                  FOLDERS[folderName].subfolders.map((subfolderName) => (
                    <div key={subfolderName}>
                      {/* Subfolder */}
                      <FolderItem
                        name={subfolderName}
                        isOpen={selectedSubfolder === subfolderName}
                        hasArrow={subfolderName === 'About' || subfolderName === 'Projects'}
                        onClick={() => handleSubfolderClick(subfolderName)}
                      />

                      {/* Files - Indented under Subfolder */}
                      {selectedSubfolder === subfolderName && (
                        <div className="ml-6 space-y-[12px] mt-[12px]">
                          {subfolderName === 'Projects' && CASE_STUDIES['Projects'] ? (
                            Object.keys(CASE_STUDIES['Projects']).map((caseStudyName) => (
                              <FileItem
                                key={caseStudyName}
                                name={caseStudyName}
                                isSelected={selectedCaseStudy === caseStudyName}
                                onClick={() => handleCaseStudyClick(caseStudyName)}
                              />
                            ))
                          ) : (
                            FILES[subfolderName]?.map((fileName) => (
                              <FileItem
                                key={fileName}
                                name={fileName}
                                isSelected={selectedFile === fileName}
                                onClick={() => handleFileClick(fileName)}
                              />
                            ))
                          )}
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  // Show files directly if no subfolders
                  FILES[folderName]?.map((fileName) => (
                    <FileItem
                      key={fileName}
                      name={fileName}
                      isSelected={selectedFile === fileName}
                      onClick={() => handleFileClick(fileName)}
                    />
                  ))
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default ExplorerBody;
